// User service for user ID generation and get-started submission

import { api } from '@/api/client';
import type { UserIdResponse, AddressData, PersonalInfo, PropertyData } from '@/types';
import { identifyUser } from '@/utils/analytics';
import { logger } from '@/utils/logger';

export async function generateUserId(): Promise<string | null> {
  const response = await api.post<UserIdResponse>('/api/2turniton/users/generate-id');

  if (response.success && response.data) {
    // Handle multiple response formats
    const userId = response.data.userId || (response.data as unknown as { data?: { userId?: string } }).data?.userId;
    if (userId) {
      identifyUser(userId);
      return userId;
    }
  }

  logger.error({ area: 'user' }, 'Failed to generate user ID: ' + (response.error || 'unknown'));
  return null;
}

export interface GetStartedRequest {
  userId: string;
  address: string;
  unit?: string;
  city: string;
  state: string;
  zip: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  rentOrOwn: 'Rent' | 'Own';
  targetMoveInDate: string;
  propertyType: string;
  isBusiness: boolean;
  analyticsId: string;
}

export interface AvailableService {
  serviceName: string;
  isAvailable: boolean;
  planId?: string;
  vendorName?: string;
}

export interface GetStartedResponse {
  userId?: string;
  availableServices?: AvailableService[];
  success?: boolean;
  data?: {
    availableServices?: AvailableService[];
  };
}

export interface GetStartedResult {
  success: boolean;
  availableServices: AvailableService[];
  error?: string;
}

/**
 * Submit get-started to register user with address and personal info
 * This should be called after personal info is collected (Step 4 in original flow)
 */
export async function submitGetStarted(
  userId: string,
  addressData: AddressData,
  personalInfo: PersonalInfo,
  propertyData?: PropertyData | null
): Promise<GetStartedResult> {
  try {
    const requestBody: GetStartedRequest = {
      userId,
      address: addressData.street || addressData.fullAddress,
      unit: addressData.unit || '',
      city: addressData.city,
      state: addressData.stateCode,
      zip: addressData.zip,
      firstName: personalInfo.firstName,
      lastName: personalInfo.lastName,
      phoneNumber: personalInfo.phone,
      emailAddress: personalInfo.email,
      rentOrOwn: personalInfo.rentOrOwn,
      targetMoveInDate: personalInfo.moveDate,
      propertyType: propertyData?.propertyType || personalInfo.propertyType || 'Residential',
      isBusiness: personalInfo.propertyType === 'Business',
      analyticsId: `analytics-${userId}`,
    };

    logger.info({ area: 'user' }, 'Submitting get-started for user: ' + userId);

    const response = await api.post<GetStartedResponse>(
      '/api/2turniton/users/get-started',
      requestBody,
      { userId }
    );

    logger.info({ area: 'user' }, 'Get-started response received');

    if (response.success && response.data) {
      // Handle both response formats:
      // Format 1: { success: true, data: { availableServices: [...] } }
      // Format 2: { userId: "...", availableServices: [...] }
      const data = response.data;
      const availableServices = data.availableServices || data.data?.availableServices || [];

      return {
        success: true,
        availableServices,
      };
    }

    return {
      success: false,
      availableServices: [],
      error: response.error || 'Failed to submit get-started',
    };
  } catch (error) {
    logger.error({ area: 'user' }, 'Get-started error', error instanceof Error ? error : undefined);
    return {
      success: false,
      availableServices: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
